import React, { useMemo, useState } from 'react';
import { Box, Grid, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { CaregiverData, LeadCaregiverEntry, LeadDetailItem } from './types';
import CustomAccordion from './ui/CustomAccordion';
import InfoField from './Table/InfoField';
import type { ExpandedViewItem } from '../LeadManagement/LeadManagement.types';
import { useGetTrackingParametersByEnrollmentIdQuery } from '../../features/lma/ccPatientApi';

const LEAD_DETAIL_FIELDS: Array<{
  label: string;
  get: (d: LeadDetailItem) => string | undefined;
}> = [
  { label: 'Associated Protocols', get: (d) => d.associatedProtocols ?? undefined },
  { label: 'Qualified Protocols', get: (d) => d.qualifiedProtocols ?? undefined },
  {
    label: 'Site Name',
    get: (d) =>
      typeof d.siteName === 'string'
        ? d.siteName
        : (d.siteName && typeof d.siteName === 'object' ? d.siteName.siteName : undefined) ??
          undefined,
  },
  { label: 'Patient Type', get: (d) => d.patientType ?? undefined },
  { label: 'Patient Referral Status', get: (d) => d.patientReferralStatus ?? undefined },
  { label: 'Condition Of Interests', get: (d) => d.conditionOfInterests ?? undefined },
  { label: 'Ethnicity', get: (d) => d.ethnicity ?? undefined },
];

const DEFAULT_CAREGIVER_FIELDS: Array<{ key: keyof CaregiverData; label: string }> = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phoneNumber', label: 'Phone Number' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipcode', label: 'Zipcode' },
];

const DEFAULT_TRACKING_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'utmSource', label: 'UTM Source' },
  { key: 'utmMedium', label: 'UTM Medium' },
  { key: 'utmCampaign', label: 'UTM Campaign' },
  { key: 'utmTerm', label: 'UTM Term' },
  { key: 'utmContent', label: 'UTM Content' },
  { key: 'sid', label: 'sID' },
  { key: 'psecId', label: 'psecID' },
  { key: 'gclid', label: 'GCLID' },
  { key: 'fbclid', label: 'FBCLID' },
  { key: 'atid', label: 'ATID' },
];

type TrackingFieldConfig = ExpandedViewItem[] | Array<{ key: string; label: string }>;

function LeadIdInfoAction({ leadId }: { leadId: number }) {
  return (
    <Tooltip title={`Lead ID: ${leadId}`} placement="top" arrow>
      <IconButton size="small" sx={{ p: 0.25 }} aria-label="Show lead ID">
        <InfoOutlinedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// LeadAccordionItem -- owns tracking API call per enrollmentId, fires on open
// ---------------------------------------------------------------------------
interface LeadAccordionItemProps {
  detail: LeadDetailItem;
  idx: number;
  leadHeadingText?: string;
  leadDetailFields: Array<{ label: string; get: (d: LeadDetailItem) => string | undefined }>;
  isLargeScreen: boolean;
  caregiver?: NonNullable<LeadCaregiverEntry['caregiver']>;
  caregiverFields: Array<{ key: string; label: string }>;
  trackingFields: Array<{ key: string; label: string }>;
  trackingHeaderText?: string;
  initiallyOpen: boolean;
  caregiverHeaderText?: string;
}

const LeadAccordionItem: React.FC<LeadAccordionItemProps> = ({
  detail,
  idx,
  leadHeadingText,
  leadDetailFields,
  isLargeScreen,
  caregiver,
  caregiverFields,
  trackingFields,
  trackingHeaderText,
  initiallyOpen,
  caregiverHeaderText,
}) => {
  const [hasFetched, setHasFetched] = useState(initiallyOpen);
  const [isOpen, setIsOpen] = useState(initiallyOpen);

  const { data: trackingParamsData } = useGetTrackingParametersByEnrollmentIdQuery(
    detail.enrollmentId || '',
    { skip: !hasFetched || !detail.enrollmentId }
  );

  const hasTrackingData =
    trackingParamsData != null &&
    Object.keys(trackingParamsData).length > 0 &&
    trackingFields.some((f) => {
      const v = (trackingParamsData as any)[f.key];
      return v !== null && v !== undefined && v !== '';
    });

  const hasCaregiverData =
    caregiver != null &&
    Object.keys(caregiver).length > 0 &&
    caregiverFields.some((f) => {
      const v = (caregiver as any)[f.key];
      return v !== null && v !== undefined && v !== '';
    });

  const handleToggle = () => {
    if (!isOpen && !hasFetched && detail.enrollmentId) {
      setHasFetched(true);
    }
    setIsOpen((v) => !v);
  };

  return (
    <CustomAccordion
      key={`${detail.leadId}-${detail.enrollmentId}-${idx}`}
      title={`${leadHeadingText || 'Lead'} ${idx + 1}`}
      expanded={isOpen}
      onChange={handleToggle}
      summaryActions={<LeadIdInfoAction leadId={detail.leadId} />}
      detailsSx={{ p: 1.25 }}
    >
      <Grid container spacing={1.25}>
        {leadDetailFields.map(({ label, get }) => (
          <Grid item xs={12} sm={6} md={4} lg={isLargeScreen ? 3 : 4} key={label}>
            <InfoField label={label} value={get(detail)} />
          </Grid>
        ))}
      </Grid>

      {hasCaregiverData ? (
        <Box sx={{ mt: 2, pt: 1.25, borderTop: '1px solid #e9e9e9' }}>
          <Box
            sx={{
              mt: 1.25,
              boxShadow: 'none',
              border: (theme: any) => `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
            }}
          >
            <Box
              sx={{
                backgroundColor: (theme: any) => theme.palette.neutral1?.main || '#F6F7FB',
                fontWeight: 600,
                py: 1.5,
                px: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  mr: 0.5,
                }}
              >
                <Box component="span" sx={{ fontSize: '14px', fontWeight: 600 }}>
                  {caregiverHeaderText || 'Caregiver Information'}
                </Box>
                <LeadIdInfoAction leadId={detail.leadId} />
              </Box>
            </Box>
            <Box sx={{ p: 1.25 }}>
              <Grid container spacing={1.25}>
                {caregiverFields.map((field) => (
                  <Grid item xs={12} sm={6} md={4} lg={isLargeScreen ? 3 : 4} key={field.key}>
                    <InfoField label={field.label} value={(caregiver as any)[field.key]} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      ) : null}

      {hasTrackingData ? (
        <Box sx={{ mt: 2, pt: 1.25, borderTop: '1px solid #e9e9e9' }}>
          <Box
            sx={{
              mt: 1.25,
              boxShadow: 'none',
              border: (theme: any) => `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
            }}
          >
            <Box
              sx={{
                backgroundColor: (theme: any) => theme.palette.neutral1?.main || '#F6F7FB',
                fontWeight: 600,
                py: 1.5,
                px: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  mr: 0.5,
                }}
              >
                <Box component="span" sx={{ fontSize: '14px', fontWeight: 600 }}>
                  {trackingHeaderText || 'Tracking Parameters'}
                </Box>
                <LeadIdInfoAction leadId={detail.leadId} />
              </Box>
            </Box>
            <Box sx={{ p: 1.25 }}>
              <Grid container spacing={1.25}>
                {trackingFields.map((field) => (
                  <Grid item xs={12} sm={6} md={4} lg={isLargeScreen ? 3 : 4} key={field.key}>
                    <InfoField
                      label={field.label}
                      value={(trackingParamsData as any)?.[field.key]}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      ) : null}
    </CustomAccordion>
  );
};

// ---------------------------------------------------------------------------
// EnrollmentSpecificInformationBlock
// ---------------------------------------------------------------------------

export interface EnrollmentSpecificInformationBlockProps {
  leadDetails: LeadDetailItem[];
  leadCaregivers?: LeadCaregiverEntry[] | null;
  enrollmentFieldConfig?: ExpandedViewItem[];
  enrollmentHeaderText?: string;
  leadHeadingText?: string;
  trackingFieldConfig?: TrackingFieldConfig;
  trackingHeaderText?: string;
  caregiverHeaderText?: string;
  caregiverFieldConfig?: ExpandedViewItem[];
}

/**
 * "Enrollment Specific Information" with Lead 1...N accordions.
 * Each lead accordion fetches its own tracking parameters on first open.
 * Caregiver and Tracking sections appear nested per lead when available.
 */
export const EnrollmentSpecificInformationBlock: React.FC<
  EnrollmentSpecificInformationBlockProps
> = ({
  leadDetails,
  leadCaregivers,
  enrollmentFieldConfig,
  enrollmentHeaderText,
  leadHeadingText,
  trackingFieldConfig,
  trackingHeaderText,
  caregiverHeaderText,
  caregiverFieldConfig,
}) => {
  const isLargeScreen = useMediaQuery('(min-width:1200px)');
  const [enrollmentOpen, setEnrollmentOpen] = useState(true);

  const leadDetailFields = useMemo((): Array<{
    label: string;
    get: (d: LeadDetailItem) => string | undefined;
  }> => {
    if (enrollmentFieldConfig && enrollmentFieldConfig.length > 0) {
      return enrollmentFieldConfig.map((item) => {
        const key = item.fields.ExpandedViewItemKey?.value ?? '';
        const label = item.fields.ExpandedViewItemLabel?.value ?? key;
        return {
          label,
          get: (d: LeadDetailItem) => {
            if (key === 'sites') {
              return typeof d.siteName === 'string'
                ? d.siteName
                : (d.siteName && typeof d.siteName === 'object'
                    ? d.siteName.siteName
                    : undefined) ?? undefined;
            }
            return (d as any)[key] ?? undefined;
          },
        };
      });
    }
    return LEAD_DETAIL_FIELDS;
  }, [enrollmentFieldConfig]);

  const caregiverByLeadId = useMemo(() => {
    const map = new Map<number, NonNullable<LeadCaregiverEntry['caregiver']>>();
    for (const entry of leadCaregivers ?? []) {
      if (entry.caregiver != null) {
        map.set(Number(entry.leadId), entry.caregiver);
      }
    }
    return map;
  }, [leadCaregivers]);

  const trackingFields = useMemo((): Array<{ key: string; label: string }> => {
    if (trackingFieldConfig && trackingFieldConfig.length > 0) {
      return (trackingFieldConfig as any[]).map((item) => {
        if (item?.fields?.ExpandedViewItemKey?.value) {
          return {
            key: item.fields.ExpandedViewItemKey.value,
            label:
              item.fields.ExpandedViewItemLabel?.value || item.fields.ExpandedViewItemKey.value,
          };
        }
        return {
          key: item?.key || '',
          label: item?.label || item?.key || '',
        };
      });
    }
    return DEFAULT_TRACKING_FIELDS;
  }, [trackingFieldConfig]);
  const caregiverFields = useMemo((): Array<{ key: string; label: string }> => {
    if (caregiverFieldConfig && caregiverFieldConfig.length > 0) {
      return caregiverFieldConfig.map((item) => ({
        key: item.fields.ExpandedViewItemKey?.value ?? '',
        label: item.fields.ExpandedViewItemLabel?.value ?? '',
      }));
    }
    return DEFAULT_CAREGIVER_FIELDS.map((f) => ({ key: f.key as string, label: f.label }));
  }, [caregiverFieldConfig]);

  const allLeadIdsTooltip = useMemo(
    () => leadDetails.map((l) => l.leadId).join(', '),
    [leadDetails]
  );

  if (!leadDetails.length) {
    return null;
  }

  return (
    <CustomAccordion
      title={enrollmentHeaderText || 'Enrollment Specific Information'}
      expanded={enrollmentOpen}
      onChange={() => setEnrollmentOpen((v) => !v)}
      summaryActions={
        <Tooltip title={`Lead IDs: ${allLeadIdsTooltip || '-'}`} placement="top" arrow>
          <IconButton size="small" sx={{ p: 0.25 }} aria-label="Enrollment lead IDs">
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      }
      detailsSx={{ p: 1.25 }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {leadDetails.map((detail, idx) => (
          <LeadAccordionItem
            key={`${detail.leadId}-${detail.enrollmentId}-${idx}`}
            detail={detail}
            idx={idx}
            leadHeadingText={leadHeadingText}
            leadDetailFields={leadDetailFields}
            isLargeScreen={isLargeScreen}
            caregiver={caregiverByLeadId.get(Number(detail.leadId))}
            caregiverFields={caregiverFields}
            trackingFields={trackingFields}
            trackingHeaderText={trackingHeaderText}
            initiallyOpen={idx === 0}
            caregiverHeaderText={caregiverHeaderText}
          />
        ))}
      </Box>
    </CustomAccordion>
  );
};

export default EnrollmentSpecificInformationBlock;
